import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import {
  Check, ChevronLeft, ChevronRight, Package, Euro, Truck, MapPin, Calendar,
  FileText, MessageSquare, Loader2, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { getMaterials, getSites, createOffer } from '@/lib/api';
import { fmt2 } from '@/lib/utils';

const INCOTERMS = [
  { code: 'EXW', label: 'EXW – Ex Works (Käufer holt ab)' },
  { code: 'FCA', label: 'FCA – Free Carrier' },
  { code: 'CPT', label: 'CPT – Carriage Paid To' },
  { code: 'CIP', label: 'CIP – Carriage and Insurance Paid' },
  { code: 'DAP', label: 'DAP – Delivered at Place' },
  { code: 'DDP', label: 'DDP – Delivered Duty Paid' },
];

const STEPS = [
  { key: 'material', label: 'Material', icon: Package },
  { key: 'commercial', label: 'Preis & Konditionen', icon: Euro },
  { key: 'review', label: 'Prüfen & Senden', icon: CheckCircle2 },
];

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export function OfferWizard({ open, onClose, ad, onSubmitted }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [sites, setSites] = useState([]);
  const [materialQuery, setMaterialQuery] = useState('');
  const [form, setForm] = useState({
    material_id: null,
    sku_id: null,
    sku_label: '',
    offered_qty: '',
    quantity_unit: ad?.quantity_unit || 'kg',
    unit_price_eur: '',
    incoterm: '',
    pickup_site_id: '',
    available_from: '',
    offer_valid_until: todayPlus(14),
    message: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load own materials (scoped to user's group) + sites once dialog opens
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setMaterialQuery('');
    setForm({
      material_id: null,
      sku_id: null,
      sku_label: '',
      offered_qty: ad?.min_quantity ? String(ad.min_quantity) : '',
      quantity_unit: ad?.quantity_unit || 'kg',
      unit_price_eur: '',
      incoterm: '',
      pickup_site_id: '',
      available_from: '',
      offer_valid_until: todayPlus(14),
      message: '',
    });

    (async () => {
      try {
        const params = {
          page: 1,
          limit: 50,
          scope: 'my_group',
        };
        if (ad?.category_node_id) params.category_node_id = ad.category_node_id;
        const res = await getMaterials(params);
        setMaterials(res.data?.materials || res.data?.items || res.data || []);
      } catch (e) {
        console.error(e);
        setMaterials([]);
      }
      try {
        const s = await getSites();
        const all = s.data || [];
        const scoped = user?.group_id ? all.filter((x) => x.group_id === user.group_id) : all;
        setSites(scoped);
      } catch {
        setSites([]);
      }
    })();
  }, [open, ad, user]);

  const totalEUR = useMemo(() => {
    const q = parseFloat(form.offered_qty);
    const p = parseFloat(form.unit_price_eur);
    if (!q || !p || q <= 0 || p < 0) return 0;
    return q * p;
  }, [form.offered_qty, form.unit_price_eur]);

  const priceWithinAdRange = useMemo(() => {
    const p = parseFloat(form.unit_price_eur);
    if (!p) return null;
    if (ad?.price_min_eur != null && p < ad.price_min_eur) return false;
    if (ad?.price_max_eur != null && p > ad.price_max_eur) return false;
    return true;
  }, [form.unit_price_eur, ad]);

  const filteredMaterials = useMemo(() => {
    if (!materialQuery) return materials.slice(0, 30);
    const q = materialQuery.toLowerCase();
    return materials
      .filter((m) =>
        (m.common_title || '').toLowerCase().includes(q) ||
        (m.manufacturer_name || '').toLowerCase().includes(q) ||
        (m.manufacturer_part_number || '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [materials, materialQuery]);

  const canProceed = () => {
    if (step === 0) {
      // Material is optional; allow free-form text in Step 2. But require qty.
      return parseFloat(form.offered_qty) > 0;
    }
    if (step === 1) {
      return (
        parseFloat(form.unit_price_eur) >= 0 &&
        !!form.incoterm &&
        !!form.offer_valid_until
      );
    }
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) {
      toast.error('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createOffer({
        wanted_id: ad.wanted_id,
        sku_id: form.sku_id || null,
        material_id: form.material_id || null,
        offered_qty: parseFloat(form.offered_qty),
        quantity_unit: form.quantity_unit,
        unit_price_eur: parseFloat(form.unit_price_eur),
        incoterm: form.incoterm,
        pickup_site_id: form.pickup_site_id || null,
        available_from: form.available_from || null,
        offer_valid_until: form.offer_valid_until,
        message: form.message || null,
      });
      toast.success('Angebot erfolgreich gesendet!');
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Senden des Angebots.');
    } finally {
      setLoading(false);
    }
  };

  if (!ad) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-wizard">
        <DialogHeader>
          <DialogTitle className="font-manrope">Strukturiertes Angebot abgeben</DialogTitle>
          <DialogDescription>
            Auf Gesuch: <span className="font-medium text-foreground">{ad.material_type_description}</span>
            <span className="text-muted-foreground"> · {ad.category_name} · {ad.min_quantity} {ad.quantity_unit}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-2 py-4" data-testid="wizard-stepper">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = idx === step;
            const isDone = idx < step;
            return (
              <div key={s.key} className="flex items-center flex-1">
                <div className={`flex items-center gap-2 ${isActive ? 'text-orange-500' : isDone ? 'text-green-600' : 'text-muted-foreground'}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                    isActive ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : isDone ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-slate-300 dark:border-slate-600'
                  }`}>
                    {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${idx < step ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
              </div>
            );
          })}
        </div>

        <Separator />

        {/* Step content */}
        <div className="py-4 space-y-4 min-h-[280px]">
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-material">
              <div>
                <Label className="text-base font-semibold">Welches Material bieten Sie an?</Label>
                <p className="text-sm text-muted-foreground mt-1">Wählen Sie aus Ihrem Bestand oder lassen Sie es offen.</p>
              </div>

              <div className="space-y-2">
                <Label>Materialsuche (optional)</Label>
                <Input
                  placeholder="Titel, Hersteller oder Teilenummer suchen…"
                  value={materialQuery}
                  onChange={(e) => setMaterialQuery(e.target.value)}
                  data-testid="wizard-material-search"
                />
                <div className="border rounded-lg max-h-48 overflow-y-auto divide-y" data-testid="wizard-material-list">
                  {filteredMaterials.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground text-center">Keine passenden Materialien im eigenen Bestand.</p>
                  ) : (
                    filteredMaterials.map((m) => {
                      const selected = form.material_id === m.material_id;
                      return (
                        <button
                          key={m.material_id}
                          type="button"
                          onClick={() => {
                            if (selected) {
                              set('material_id', null);
                              set('sku_id', null);
                              set('sku_label', '');
                            } else {
                              set('material_id', m.material_id);
                              set('sku_id', null);
                              set('sku_label', m.common_title || '');
                            }
                          }}
                          className={`w-full text-left p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between gap-2 ${
                            selected ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                          }`}
                          data-testid={`wizard-material-${m.material_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{m.common_title || m.manufacturer_name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {m.manufacturer_name} · {m.material_width_mm ? `${m.material_width_mm} mm` : 'Breite n/a'}
                            </p>
                          </div>
                          {selected && <Check className="h-4 w-4 text-orange-500 shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Angebotene Menge <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.offered_qty}
                    onChange={(e) => set('offered_qty', e.target.value)}
                    data-testid="wizard-quantity"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Einheit</Label>
                  <Select value={form.quantity_unit} onValueChange={(v) => set('quantity_unit', v)}>
                    <SelectTrigger data-testid="wizard-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['kg', 'sqm', 'rolls', 'pcs', 'lm', 'pallet'].map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {ad?.min_quantity && parseFloat(form.offered_qty) > 0 && parseFloat(form.offered_qty) < ad.min_quantity && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  Ihre Menge liegt unter der vom Käufer geforderten Mindestmenge ({ad.min_quantity} {ad.quantity_unit}).
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-commercial">
              <div>
                <Label className="text-base font-semibold">Preis & Konditionen</Label>
                <p className="text-sm text-muted-foreground mt-1">Angebot vollständig — der Käufer sieht alle Daten transparent.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1"><Euro className="h-3.5 w-3.5 text-orange-500" />Stückpreis EUR <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.unit_price_eur}
                    onChange={(e) => set('unit_price_eur', e.target.value)}
                    data-testid="wizard-unit-price"
                  />
                  {ad?.price_min_eur != null || ad?.price_max_eur != null ? (
                    <p className="text-xs text-muted-foreground">
                      Käufer-Vorstellung: {ad.price_min_eur != null ? `ab ${fmt2(ad.price_min_eur)} €` : ''}
                      {ad.price_min_eur != null && ad.price_max_eur != null ? ' – ' : ''}
                      {ad.price_max_eur != null ? `bis ${fmt2(ad.price_max_eur)} €` : ''}
                    </p>
                  ) : null}
                  {priceWithinAdRange === false && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Außerhalb der Käufer-Vorstellung</p>
                  )}
                  {priceWithinAdRange === true && (
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">Im Wunschbereich des Käufers</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Gesamtpreis (live)</Label>
                  <div className="h-10 px-3 flex items-center rounded-md border bg-slate-50 dark:bg-slate-800 font-semibold text-orange-600" data-testid="wizard-total">
                    {totalEUR > 0 ? `${fmt2(totalEUR)} €` : '—'}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" />Incoterm <span className="text-red-500">*</span></Label>
                <Select value={form.incoterm} onValueChange={(v) => set('incoterm', v)}>
                  <SelectTrigger data-testid="wizard-incoterm"><SelectValue placeholder="Wählen Sie einen Incoterm" /></SelectTrigger>
                  <SelectContent>
                    {INCOTERMS.map((ic) => (
                      <SelectItem key={ic.code} value={ic.code}>{ic.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Abhol-/Lieferstandort</Label>
                  <Select value={form.pickup_site_id || 'none'} onValueChange={(v) => set('pickup_site_id', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="wizard-pickup-site"><SelectValue placeholder="Standort wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keiner —</SelectItem>
                      {sites.map((s) => (
                        <SelectItem key={s.site_id} value={s.site_id}>{s.site_name} ({s.city})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Verfügbar ab</Label>
                  <Input
                    type="date"
                    value={form.available_from}
                    onChange={(e) => set('available_from', e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    data-testid="wizard-available-from"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Angebot gültig bis <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={form.offer_valid_until}
                  onChange={(e) => set('offer_valid_until', e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  data-testid="wizard-offer-valid-until"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Nachricht an Käufer</Label>
                <Textarea
                  placeholder="Konditionen, Qualität, Hinweise…"
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                  rows={3}
                  data-testid="wizard-message"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-review">
              <div>
                <Label className="text-base font-semibold">Angebot prüfen</Label>
                <p className="text-sm text-muted-foreground mt-1">Vor dem Absenden noch einmal kontrollieren.</p>
              </div>

              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <ReviewRow label="Material" value={form.sku_label || <span className="text-muted-foreground italic">Frei / nicht zugeordnet</span>} />
                    <ReviewRow label="Menge" value={`${form.offered_qty} ${form.quantity_unit}`} />
                    <ReviewRow label="Stückpreis" value={`${fmt2(parseFloat(form.unit_price_eur))} € / ${form.quantity_unit}`} />
                    <ReviewRow label="Gesamtpreis" value={<span className="font-bold text-orange-600">{fmt2(totalEUR)} €</span>} />
                    <ReviewRow label="Incoterm" value={form.incoterm} />
                    <ReviewRow label="Standort" value={sites.find((s) => s.site_id === form.pickup_site_id)?.site_name || '—'} />
                    <ReviewRow label="Verfügbar ab" value={form.available_from || '—'} />
                    <ReviewRow label="Gültig bis" value={form.offer_valid_until} />
                  </div>
                  {form.message && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Nachricht</p>
                        <p className="text-sm whitespace-pre-wrap">{form.message}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm">
                <FileText className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-200">Geführter Prozess</p>
                  <p className="text-blue-700 dark:text-blue-300 mt-0.5">
                    Nach dem Absenden sieht der Käufer Ihr Angebot in seiner Vergleichsansicht.
                    Bei Annahme bestätigen Sie als Verkäufer den Deal, dann wird die Transaktion erfasst.
                    Die Provision trägt der Käufer.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="wizard-cancel">
            Abbrechen
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" onClick={handleBack} disabled={loading} data-testid="wizard-back">
                <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button className="bg-orange-500 hover:bg-orange-600" onClick={handleNext} data-testid="wizard-next">
                Weiter <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button className="bg-orange-500 hover:bg-orange-600" onClick={handleSubmit} disabled={loading} data-testid="wizard-submit">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Angebot abschicken
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewRow({ label, value }) {
  return (
    <>
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium text-right">{value}</div>
    </>
  );
}
